// The documents the PQRSD module files by itself, and the pieces each one is
// built from (config/pqrsd.documents):
//
//   letterheads          membretes: .docx with header + footer and an empty body
//   defaultLetterheadId  the membrete every document uses unless it picks another
//   bodies[kind]         the document's text only, written in the RichEditor
//   merged[kind]         the text poured into its membrete — the .docx the cloud
//                        functions hand to RoyalEditorApi (see normalizeDocuments
//                        in gfunctions/pqrsd/_shared/infoRequestLogic.js)
//   answers[]            the answer templates an operator picks from when
//                        starting a response: each one a named text with its own
//                        membrete, body and merged .docx (read by
//                        gfunctions/pqrsd/startPqrsdResponseFlow)
//
// The helpers below take a "doc key": a fixed kind ("filing", …) or an answer's
// key (answerKey(id) → "answer_<id>"), so the membrete/rebuild machinery treats
// both the same way.
//
// Kept free of React/MUI so the tag lists can be tested against the backend.

// Merge tags the backend actually fills in. Advertising a tag that is never sent
// is worse than not listing it: an unfilled tag is not replaced, so the literal
// "<<etiqueta>>" survives into the radicado PDF. The acts' own outgoing radicado
// is deliberately absent — the container stamps it on the header table.
const ACT_COMMON_TAGS = [
  "radicado_pqrsd", "fecha", "fecha_radicacion", "asunto", "nombre_solicitante",
  "apellido_solicitante", "email", "tipo_solicitud", "fundamento",
  "firmante", "cargo_firmante", "dependencia_firmante", "proyecto", "reviso",
];

export const DOCUMENT_KINDS = [
  {
    key: "filing",
    label: "Radicación de la PQRSD",
    hint: "Se genera al radicar una PQRSD desde el portal público o la ventanilla.",
    whenMissing: "Mientras no esté listo, la radicación usa el texto predeterminado sin membrete.",
    // handlePqrsd/index.js, `const tags = {`.
    tags: [
      "radicado", "fecha", "asunto", "tipo_solicitud", "nombre_solicitante",
      "apellido_solicitante", "tipo_id", "num_id", "type_person", "email",
      "body", "annexes_names",
    ],
  },
  {
    key: "infoRequest",
    label: "Requerimiento de información",
    hint: "Se radica al solicitar información al ciudadano (artículo 17 de la Ley 1755 de 2015).",
    whenMissing: "Mientras no esté listo, el requerimiento usa el texto predeterminado sin membrete.",
    tags: [
      ...ACT_COMMON_TAGS,
      "puntos_requeridos", "numero_puntos", "observaciones",
      "fecha_limite", "dias_plazo", "enlace_respuesta",
    ],
  },
  {
    key: "desistimiento",
    label: "Acto que decreta el desistimiento",
    hint: "Se radica al archivar la PQRSD por desistimiento tácito.",
    whenMissing: "Mientras no esté listo, el acto usa el texto predeterminado sin membrete.",
    tags: [
      ...ACT_COMMON_TAGS,
      "motivacion", "radicado_requerimiento", "fecha_requerimiento",
      "fecha_limite", "puntos_requeridos", "fecha_limite_recurso",
    ],
  },
  {
    key: "appealDecision",
    label: "Acto que resuelve el recurso de reposición",
    hint: "Se radica al reponer o confirmar el desistimiento cuando el ciudadano interpone el recurso.",
    whenMissing: "Mientras no esté listo, la decisión usa el texto predeterminado sin membrete.",
    tags: [
      ...ACT_COMMON_TAGS,
      "radicado_desistimiento", "fecha_desistimiento", "fecha_recurso",
      "motivos_recurso", "respuestas_recurso", "decision", "motivacion",
    ],
  },
];

export const DOCUMENT_KIND_KEYS = DOCUMENT_KINDS.map((k) => k.key);

// Tags startPqrsdResponseFlow/answerTags.js fills for every answer. The PQRSD
// being answered, the answer itself, the citizen (under both the _solicitante
// names of the other PQRSD documents and the _destinatario names of the process
// templates) and the people in the chain. The answer's own radicado is stamped
// on the header table by RoyalEditorApi, not offered as a tag.
export const ANSWER_TAGS = [
  "radicado_pqrsd", "fecha_radicacion", "asunto_pqrsd", "descripcion_pqrsd",
  "tipo_solicitud", "anexos_pqrsd",
  "asunto", "fecha", "tipo_respuesta",
  "nombre_solicitante", "apellido_solicitante", "tipo_id", "num_id", "type_person", "email",
  "nombre_destinatario", "apellido_destinatario", "correo_destinatario",
  "firmante", "cargo_firmante", "dependencia_firmante", "proyecto", "reviso",
];

export const MAX_ANSWER_TEMPLATES = 30;

const ANSWER_KEY_PREFIX = "answer_";
export const answerKey = (id) => `${ANSWER_KEY_PREFIX}${id}`;
export const isAnswerKey = (key) => typeof key === "string" && key.startsWith(ANSWER_KEY_PREFIX);
const answerIdOf = (key) => key.slice(ANSWER_KEY_PREFIX.length);
// The id ends up in a storage path and a doc key: keep it a plain slug.
const ANSWER_ID = /^[A-Za-z0-9_-]{1,64}$/;

export const MAX_TEMPLATE_BYTES = 8 * 1024 * 1024;

// Storage paths, under documents/ (saveFile). Fixed per piece, so replacing a
// membrete or rebuilding a document overwrites the previous file.
export const letterheadPath = (id) => `templates/pqrsd_letterhead_${id}`;
export const bodyPath = (kind) => `templates/pqrsd_body_${kind}`;
export const mergedPath = (kind) => `templates/pqrsd_${kind}`;

const safeUrl = (value) => (typeof value === "string" && /^https?:\/\//i.test(value) ? value : "");
const clamp = (value, max) => (typeof value === "string" ? value.trim().slice(0, max) : "");

export function normalizeDocumentsConfig(raw) {
  const d = raw && typeof raw === "object" ? raw : {};
  const seen = new Set();
  const letterheads = (Array.isArray(d.letterheads) ? d.letterheads : [])
    .map((l) => ({
      id: clamp(l?.id, 64),
      name: clamp(l?.name, 160),
      url: safeUrl(l?.url),
      updatedAt: clamp(l?.updatedAt, 40),
    }))
    .filter((l) => {
      if (!l.id || !l.url || seen.has(l.id)) return false;
      seen.add(l.id);
      return true;
    });
  const exists = (id) => letterheads.some((l) => l.id === id);
  const bodies = {};
  const merged = {};
  DOCUMENT_KIND_KEYS.forEach((kind) => {
    const b = d.bodies?.[kind] || {};
    bodies[kind] = {
      url: safeUrl(b.url),
      // "" = the default membrete. A pick that no longer exists falls back too.
      letterheadId: exists(b.letterheadId) ? b.letterheadId : "",
      updatedAt: clamp(b.updatedAt, 40),
    };
    const m = d.merged?.[kind] || {};
    merged[kind] = {
      url: safeUrl(m.url),
      letterheadId: clamp(m.letterheadId, 64),
      builtAt: clamp(m.builtAt, 40),
    };
  });
  const seenAnswers = new Set();
  const answers = (Array.isArray(d.answers) ? d.answers : [])
    .map((a) => ({
      id: clamp(a?.id, 64),
      name: clamp(a?.name, 160),
      active: a?.active !== false,
      letterheadId: exists(a?.letterheadId) ? a.letterheadId : "",
      body: { url: safeUrl(a?.body?.url), updatedAt: clamp(a?.body?.updatedAt, 40) },
      merged: {
        url: safeUrl(a?.merged?.url),
        letterheadId: clamp(a?.merged?.letterheadId, 64),
        builtAt: clamp(a?.merged?.builtAt, 40),
      },
    }))
    .filter((a) => {
      if (!ANSWER_ID.test(a.id) || !a.name || seenAnswers.has(a.id)) return false;
      seenAnswers.add(a.id);
      return true;
    });
  return {
    letterheads,
    defaultLetterheadId: exists(d.defaultLetterheadId) ? d.defaultLetterheadId : letterheads[0]?.id || "",
    bodies,
    merged,
    answers,
  };
}

// Every document the admin builds: the fixed ones, then each answer template.
export function allDocKeys(docs) {
  return [...DOCUMENT_KIND_KEYS, ...(docs.answers || []).map((a) => answerKey(a.id))];
}

export function findAnswer(docs, key) {
  if (!isAnswerKey(key)) return null;
  const id = answerIdOf(key);
  return (docs.answers || []).find((a) => a.id === id) || null;
}

// { url, letterheadId, updatedAt } of a document's text, whatever its kind.
export function bodyOf(docs, key) {
  if (isAnswerKey(key)) {
    const a = findAnswer(docs, key);
    return a ? { url: a.body.url, letterheadId: a.letterheadId, updatedAt: a.body.updatedAt } : null;
  }
  return docs.bodies[key] || null;
}

// { url, letterheadId, builtAt } of a document's merged .docx.
export function mergedOf(docs, key) {
  if (isAnswerKey(key)) return findAnswer(docs, key)?.merged || null;
  return docs.merged[key] || null;
}

const mapAnswer = (docs, key, update) => ({
  ...docs,
  answers: (docs.answers || []).map((a) => (a.id === answerIdOf(key) ? update(a) : a)),
});

// Applies a patch of { url, letterheadId, updatedAt } to a document's text.
export function withBody(docs, key, patch) {
  if (isAnswerKey(key)) {
    return mapAnswer(docs, key, (a) => {
      const { letterheadId, ...body } = patch;
      return {
        ...a,
        letterheadId: letterheadId === undefined ? a.letterheadId : letterheadId,
        body: { ...a.body, ...body },
      };
    });
  }
  return { ...docs, bodies: { ...docs.bodies, [key]: { ...docs.bodies[key], ...patch } } };
}

export function withMerged(docs, key, merged) {
  if (isAnswerKey(key)) return mapAnswer(docs, key, (a) => ({ ...a, merged }));
  return { ...docs, merged: { ...docs.merged, [key]: merged } };
}

export function effectiveLetterheadId(docs, key) {
  return bodyOf(docs, key)?.letterheadId || docs.defaultLetterheadId || "";
}

export function kindsUsingLetterhead(docs, letterheadId) {
  return allDocKeys(docs).filter((key) => effectiveLetterheadId(docs, key) === letterheadId);
}

// ready | no_text | no_letterhead | stale. Stale means the stored merged file
// no longer reflects its text or membrete (a rebuild failed or never ran).
// Timestamps are ISO strings, so they compare as strings.
export function documentStatus(docs, kind) {
  const body = bodyOf(docs, kind);
  if (!body?.url) return "no_text";
  const letterheadId = effectiveLetterheadId(docs, kind);
  const letterhead = docs.letterheads.find((l) => l.id === letterheadId);
  if (!letterhead) return "no_letterhead";
  const merged = mergedOf(docs, kind);
  if (
    !merged?.url ||
    merged.letterheadId !== letterheadId ||
    merged.builtAt < body.updatedAt ||
    merged.builtAt < letterhead.updatedAt
  ) {
    return "stale";
  }
  return "ready";
}
